/**
 * PDPA Compliance Toolkit - HYB MASTER SHOP
 * Connects dashboard to PDPA consent and data request APIs
 */


import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Shield,
  FileCheck,
  Download,
  Phone,
  Calendar,
  Mail,
  AlertTriangle,
} from 'lucide-react';
import {
  fetchPdpaConsents,
  fetchPdpaRequests,
  createPdpaConsent,
  createPdpaRequest,
  updatePdpaRequest,
  createPdpaUpload,
  PdpaConsent,
  PdpaRequest,
} from '../api/admin';

interface ConsentFormValues {
  userPhone: string;
  consentType: string;
  consentVersion: string;
  consentMethod: string;
  collectedAt: string;
  evidenceFile?: FileList;
}

interface RequestFormValues {
  userPhone: string;
  requestType: string;
  submittedAt: string;
  notes?: string;
}

export function PDPACompliance() {
  const queryClient = useQueryClient();

  const consentForm = useForm<ConsentFormValues>({
    defaultValues: {
      userPhone: '',
      consentType: 'terms',
      consentVersion: '1.0',
      consentMethod: 'app_registration',
      collectedAt: new Date().toISOString().slice(0, 16),
    },
  });

  const requestForm = useForm<RequestFormValues>({
    defaultValues: {
      userPhone: '',
      requestType: 'access',
      submittedAt: new Date().toISOString().slice(0, 16),
    },
  });

  const consentsQuery = useQuery({
    queryKey: ['pdpa-consents'],
    queryFn: () => fetchPdpaConsents({ limit: 50 }),
  });

  const requestsQuery = useQuery({
    queryKey: ['pdpa-requests'],
    queryFn: () => fetchPdpaRequests({ limit: 50 }),
  });

  const createConsentMutation = useMutation({
    mutationFn: async (payload: ConsentFormValues) => {
      let evidenceUrl: string | undefined;
      const file = payload.evidenceFile?.[0];
      if (file) {
        const upload = await createPdpaUpload({ fileName: file.name, contentType: file.type });
        const formData = new FormData();
        Object.entries(upload.upload.fields).forEach(([key, value]) => formData.append(key, String(value)));
        formData.append('file', file);
        await fetch(upload.upload.url, { method: 'POST', body: formData });
        evidenceUrl = upload.fileUrl;
      }
      return createPdpaConsent({
        userPhone: payload.userPhone,
        consentType: payload.consentType,
        consentVersion: payload.consentVersion,
        consentMethod: payload.consentMethod,
        collectedAt: new Date(payload.collectedAt).toISOString(),
        evidenceUrl,
      });
    },
    onSuccess: () => {
      toast.success('บันทึกหลักฐานการยินยอมแล้ว');
      consentForm.reset({
        userPhone: '',
        consentType: 'terms',
        consentVersion: '1.0',
        consentMethod: 'app_registration',
        collectedAt: new Date().toISOString().slice(0, 16),
      });
      queryClient.invalidateQueries({ queryKey: ['pdpa-consents'] });
    },
    onError: () => toast.error('บันทึกหลักฐานไม่สำเร็จ'),
  });

  const createRequestMutation = useMutation({
    mutationFn: (payload: RequestFormValues) =>
      createPdpaRequest({
        userPhone: payload.userPhone,
        requestType: payload.requestType,
        submittedAt: new Date(payload.submittedAt).toISOString(),
        notes: payload.notes,
      }),
    onSuccess: () => {
      toast.success('บันทึกคำขอของเจ้าของข้อมูลแล้ว');
      requestForm.reset({
        userPhone: '',
        requestType: 'access',
        submittedAt: new Date().toISOString().slice(0, 16),
        notes: '',
      });
      queryClient.invalidateQueries({ queryKey: ['pdpa-requests'] });
    },
    onError: () => toast.error('บันทึกคำขอไม่สำเร็จ'),
  });

  const resolveRequestMutation = useMutation({
    mutationFn: (requestId: string) => updatePdpaRequest(requestId, { status: 'resolved' }),
    onSuccess: () => {
      toast.success('ทำเครื่องหมายว่าเสร็จเรียบร้อย');
      queryClient.invalidateQueries({ queryKey: ['pdpa-requests'] });
    },
    onError: () => toast.error('ไม่สามารถอัปเดตสถานะคำขอได้'),
  });

  const consents: PdpaConsent[] = consentsQuery.data ?? [];
  const requests: PdpaRequest[] = requestsQuery.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Shield className="h-6 w-6 text-yellow-500" />
            การจัดการ PDPA
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            บันทึกหลักฐานการยินยอมและติดตามคำขอจากเจ้าของข้อมูล ลูกค้าเห็นประสบการณ์ที่โปร่งใส ใช้งานง่าย
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            if (!consents.length && !requests.length) {
              toast('ใช้ฟอร์มด้านล่างเพื่อบันทึกหลักฐานชุดแรก', { icon: 'ℹ️' });
            }
            const payload = {
              exportedAt: new Date().toISOString(),
              consents,
              requests,
            };
            const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `pdpa-evidence-${new Date().toISOString().split('T')[0]}.json`;
            link.click();
            URL.revokeObjectURL(url);
          }}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          <Download className="h-4 w-4" />
          ดาวน์โหลดหลักฐานทั้งหมด
        </button>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <SummaryCard
          icon={<Shield className="h-6 w-6 text-yellow-500" />}
          title="หลักฐานการยินยอม"
          value={`${consents.length} รายการ`}
          detail="จัดเก็บไว้ในฐานข้อมูลและสามารถค้นหาได้ทุกเมื่อ"
        />
        <SummaryCard
          icon={<FileCheck className="h-6 w-6 text-emerald-500" />}
          title="คำขอที่รอดำเนินการ"
          value={`${requests.filter(item => item.status !== 'resolved').length} รายการ`}
          detail="ติดตามสถานะการตอบสนองภายใน SLA"
        />
        <SummaryCard
          icon={<AlertTriangle className="h-6 w-6 text-amber-500" />}
          title="คำแนะนำการปฏิบัติตาม"
          value="ทบทวนทุกสัปดาห์"
          detail="ตรวจสอบคำขอค้างเกิน 30 วันและจัดลำดับความสำคัญ"
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
        <div className="space-y-4 rounded-xl bg-white dark:bg-gray-800 p-6 shadow-sm ring-1 ring-gray-100 dark:ring-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">บันทึกการยินยอม</h2>
          <form
            onSubmit={consentForm.handleSubmit(values => createConsentMutation.mutate(values))}
            className="grid gap-4 md:grid-cols-2"
          >
            <div className="md:col-span-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                <Phone className="h-4 w-4" /> หมายเลขโทรศัพท์ (รูปแบบ 0XXXXXXXXX)
              </label>
              <input
                {...consentForm.register('userPhone', {
                  required: 'กรุณากรอกหมายเลขโทรศัพท์',
                  pattern: { value: /^0\d{9}$/u, message: 'กรุณากรอกหมายเลขโทรศัพท์ให้ถูกต้อง' },
                })}
                className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-yellow-500 dark:focus:ring-orange-500"
                placeholder="0812345678"
              />
              {consentForm.formState.errors.userPhone && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">{consentForm.formState.errors.userPhone.message}</p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">ประเภทความยินยอม</label>
              <select
                {...consentForm.register('consentType')}
                className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-yellow-500 dark:focus:ring-orange-500"
              >
                <option value="terms">Terms & Conditions</option>
                <option value="privacy">Privacy Policy</option>
                <option value="marketing">Marketing Opt-in</option>
              </select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">เวอร์ชัน</label>
              <input
                {...consentForm.register('consentVersion', { required: 'กรุณาระบุเวอร์ชัน' })}
                className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-yellow-500 dark:focus:ring-orange-500"
              />
              {consentForm.formState.errors.consentVersion && (
                <p className="mt-1 text-xs text-red-600">{consentForm.formState.errors.consentVersion.message}</p>
              )}
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <Calendar className="h-4 w-4" /> เวลาให้ความยินยอม
              </label>
              <input
                type="datetime-local"
                {...consentForm.register('collectedAt')}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">วิธีจัดเก็บหลักฐาน</label>
              <select
                {...consentForm.register('consentMethod')}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
              >
                <option value="app_registration">ผ่านแอปอัตโนมัติ</option>
                <option value="manual_upload">อัปโหลดด้วยตนเอง</option>
              </select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">แนบหลักฐาน (ถ้ามี)</label>
              <input
                type="file"
                accept="image/*,.pdf"
                {...consentForm.register('evidenceFile')}
                className="mt-1 w-full text-sm"
              />
            </div>

            <div className="md:col-span-2 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => consentForm.reset()}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
              >
                ล้างฟอร์ม
              </button>
              <button
                type="submit"
                className="rounded-lg bg-yellow-500 px-4 py-2 text-sm font-semibold text-white hover:bg-yellow-600 disabled:opacity-50"
                disabled={createConsentMutation.isLoading}
              >
                {createConsentMutation.isLoading ? 'กำลังบันทึก...' : 'บันทึกหลักฐาน'}
              </button>
            </div>
          </form>

          <div className="mt-6 max-h-80 overflow-y-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">หมายเลข</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">ประเภท</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">เวอร์ชัน</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">เวลา</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">หลักฐาน</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {consents.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-gray-500">
                      ยังไม่มีหลักฐาน กรุณาบันทึกจากข้อมูลจริงหรือไฟล์ที่ส่งมา
                    </td>
                  </tr>
                ) : (
                  consents.map(consent => (
                    <tr key={consent.id}>
                      <td className="whitespace-nowrap px-4 py-2 text-gray-900">{consent.userPhone}</td>
                      <td className="px-4 py-2 text-gray-600">{consent.consentType}</td>
                      <td className="px-4 py-2 text-gray-600">{consent.consentVersion}</td>
                      <td className="whitespace-nowrap px-4 py-2 text-gray-600">
                        {new Date(consent.collectedAt).toLocaleString('th-TH')}
                      </td>
                      <td className="px-4 py-2 text-gray-600">
                        {consent.evidenceUrl ? (
                          <a href={consent.evidenceUrl} target="_blank" rel="noreferrer" className="text-yellow-600 hover:underline">
                            เปิดดูหลักฐาน
                          </a>
                        ) : (
                          <span className="text-xs text-gray-400">ไม่มีไฟล์แนบ</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="space-y-4 rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">คำขอของเจ้าของข้อมูล</h2>
          <form
            onSubmit={requestForm.handleSubmit(values => createRequestMutation.mutate(values))}
            className="space-y-3"
          >
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <Phone className="h-4 w-4" /> หมายเลขโทรศัพท์
              </label>
              <input
                {...requestForm.register('userPhone', {
                  required: 'กรุณากรอกหมายเลขโทรศัพท์',
                  pattern: { value: /^0\d{9}$/u, message: 'กรุณากรอกหมายเลขโทรศัพท์ให้ถูกต้อง' },
                })}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
              {requestForm.formState.errors.userPhone && (
                <p className="mt-1 text-xs text-red-600">{requestForm.formState.errors.userPhone.message}</p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">ประเภทคำขอ</label>
              <select
                {...requestForm.register('requestType')}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
              >
                <option value="access">ขอเข้าถึงข้อมูล</option>
                <option value="deletion">ขอลบข้อมูล</option>
                <option value="correction">ขอแก้ไขข้อมูล</option>
                <option value="portability">ขอรับข้อมูล (Data Portability)</option>
              </select>
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <Calendar className="h-4 w-4" /> เวลาที่ได้รับคำขอ
              </label>
              <input
                type="datetime-local"
                {...requestForm.register('submittedAt')}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <Mail className="h-4 w-4" /> หมายเหตุ (ถ้ามี)
              </label>
              <textarea
                {...requestForm.register('notes')}
                rows={3}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                placeholder="อธิบายช่องทางติดต่อและรายละเอียดเพิ่มเติม"
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => requestForm.reset()}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
              >
                ล้างฟอร์ม
              </button>
              <button
                type="submit"
                className="rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-600 disabled:opacity-50"
                disabled={createRequestMutation.isLoading}
              >
                {createRequestMutation.isLoading ? 'กำลังบันทึก...' : 'บันทึกคำขอ'}
              </button>
            </div>
          </form>

          <div className="rounded-lg border border-gray-200">
            <div className="border-b border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700">
              คำขอล่าสุด
            </div>
            <ul className="divide-y divide-gray-200">
              {requests.length === 0 ? (
                <li className="px-4 py-6 text-center text-sm text-gray-500">ยังไม่มีคำขอ</li>
              ) : (
                requests.map(request => (
                  <li key={request.id} className="flex items-start justify-between gap-3 px-4 py-3">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {request.requestType} • {request.userPhone}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(request.submittedAt).toLocaleString('th-TH')}
                      </p>
                      {request.notes && <p className="mt-1 text-xs text-gray-600">{request.notes}</p>}
                    </div>
                    <button
                      type="button"
                      onClick={() => resolveRequestMutation.mutate(request.id)}
                      className={`rounded-full px-3 py-1 text-xs font-medium ${request.status === 'resolved' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600 hover:bg-emerald-50 hover:text-emerald-600'}`}
                    >
                      {request.status === 'resolved' ? 'ดำเนินการแล้ว' : 'ทำเครื่องหมายว่าเสร็จ'}
                    </button>
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({
  icon,
  title,
  value,
  detail,
}: {
  icon: React.ReactNode;
  title: string;
  value: string;
  detail: string;
}) {
  return (
    <div className="flex items-center gap-4 rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-100">
      <div className="rounded-full bg-yellow-50 p-3">{icon}</div>
      <div>
        <p className="text-sm text-gray-500">{title}</p>
        <p className="text-xl font-semibold text-gray-900">{value}</p>
        <p className="text-xs text-gray-500">{detail}</p>
      </div>
    </div>
  );
}
